import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ServiceExecutionModel } from 'app/app-model/diagnostic-service/service-execution.model';
import { AuthService } from 'app/modules/authentication/services/auth.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-ecu-specific-function',
  templateUrl: './ecu-specific-function.component.html',
  styleUrls: ['./ecu-specific-function.component.css']
})
export class EcuSpecificFunctionComponent implements OnInit, OnDestroy {

  private subscriptions = new Subscription();

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private _isReadOnly = false;
  public get isReadOnly(): boolean {
    return this._isReadOnly;
  }

  @Input()
  public set isReadOnly(v: boolean) {
    this._isReadOnly = v === true;
    this.enableOrDisableInputControls();
  }

  private _ecuSpecificFunctionServiceExecution: ServiceExecutionModel;
  public get ecuSpecificFunctionServiceExecution(): ServiceExecutionModel {
    return this._ecuSpecificFunctionServiceExecution;
  }

  @Input()
  public set ecuSpecificFunctionServiceExecution(v: ServiceExecutionModel) {
    this._ecuSpecificFunctionServiceExecution = v;

    if (this._ecuSpecificFunctionServiceExecution) {
      this.initEcuSpecificFunction();
    } else {
      this.disableEcuSpecificFunction();
    }
  }

  @Output()
  ecuSpecificFunctionCommandChanged: EventEmitter<string> = new EventEmitter();

  ecuSpecificFunctionControl = new FormControl();
  selectedEcuSpecificFunction: string | undefined;
  ecuSpecificFunctionEnabled: boolean = false;

  private _hasConnectedDiagnosticService = false;
  public get hasConnectedDiagnosticService(): boolean {
    return this._hasConnectedDiagnosticService;
  }

  @Input()
  public set hasConnectedDiagnosticService(v: boolean) {
    this._hasConnectedDiagnosticService = v === true;

    if (this.hasConnectedDiagnosticService && !this.ecuSpecificFunctionServiceExecution?.command) {
      this.disableEcuSpecificFunction();
    }

    this.enableOrDisableInputControls();
  }

  constructor(private authService: AuthService) { }

  get canConfirmEcuSpecificFunction(): boolean {
    return !this.isReadOnly && !this.hasConnectedDiagnosticService;
  }

  get canActivateEcuSpecificFunction(): boolean {
    return !this.isReadOnly &&
      this.canEditCategoryItems() &&
      (this.ecuSpecificFunctionEnabled || !this.hasConnectedDiagnosticService);
  }

  get showConnectedDiagnosticServicesMessage(): boolean {
    return this.hasConnectedDiagnosticService && !this.ecuSpecificFunctionEnabled;
  }

  ngOnInit(): void {
    this.controlValueChanges();
    this.enableOrDisableInputControls();
  }

  confirmEnteredEcuSpecificFunction() {
    if (this.hasConnectedDiagnosticService) {
      return;
    }

    this.ecuSpecificFunctionCommandChanged.emit(this.selectedEcuSpecificFunction || '');
  }

  onActivateEcuSpecificFunction() {
    if (!this.ecuSpecificFunctionEnabled && this.hasConnectedDiagnosticService) {
      return;
    }

    this.ecuSpecificFunctionEnabled = !this.ecuSpecificFunctionEnabled;

    if (!this.ecuSpecificFunctionEnabled) {
      this.setSelectedEcuSpecificFunction(undefined);
      this.ecuSpecificFunctionCommandChanged.emit('');
    }
  }

  private enableOrDisableInputControls() {
    if (this.isReadOnly || this.hasConnectedDiagnosticService) {
      this.ecuSpecificFunctionControl.disable({ emitEvent: false });
    } else {
      this.ecuSpecificFunctionControl.enable({ emitEvent: false });
    }
  }

  private initEcuSpecificFunction() {
    if (this.ecuSpecificFunctionServiceExecution?.command) {
      this.ecuSpecificFunctionEnabled = true;
      this.setSelectedEcuSpecificFunction(this.ecuSpecificFunctionServiceExecution.command.arguments ?? '');
    } else {
      this.disableEcuSpecificFunction();
    }
  }

  private disableEcuSpecificFunction() {
    this.ecuSpecificFunctionEnabled = false;
    this.setSelectedEcuSpecificFunction(undefined);
  }

  canEditCategoryItems(): boolean {
    return this.authService.canEditCategoryItems();
  }

  private controlValueChanges() {
    this.subscriptions.add(this.ecuSpecificFunctionControl.valueChanges.subscribe(value => {
      if (this.selectedEcuSpecificFunction !== value) {
        this.setSelectedEcuSpecificFunction(value);
      }
    }));
  }

  private setSelectedEcuSpecificFunction(value: string | undefined) {
    this.selectedEcuSpecificFunction = value;

    if (this.ecuSpecificFunctionControl.value !== value) {
      this.ecuSpecificFunctionControl.setValue(value, { emitEvent: false });
    }
  }
}
